import { useEffect, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import { RequireAuth } from "@/components/RequireAuth";
import { ErrorState } from "@/components/ErrorState";
import { AppShell } from "@/components/layout/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { AccountMovementsTable } from "@/components/journal/AccountMovementsTable";
import { useActiveCompany } from "@/lib/company-context";
import { useAccountMovements } from "@/lib/api/journal.queries";

export const Route = createFileRoute(
  "/companies/$companyId/contabilidad/mayor/$accountCode",
)({
  component: AccountMovementsPage,
});

function AccountMovementsPage() {
  return (
    <RequireAuth>
      <AppShell>
        <AccountMovementsContent />
      </AppShell>
    </RequireAuth>
  );
}

function AccountMovementsContent() {
  const { t } = useTranslation();
  const { companyId, accountCode } = Route.useParams();
  const { setActiveCompanyId } = useActiveCompany();
  const navigate = useNavigate();

  useEffect(() => {
    if (companyId) setActiveCompanyId(companyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const [periodFrom, setPeriodFrom] = useState("");
  const [periodTo, setPeriodTo] = useState("");
  const [page, setPage] = useState(1);

  const { data, isLoading, isError } = useAccountMovements(
    companyId,
    accountCode,
    {
      periodFrom: periodFrom || undefined,
      periodTo: periodTo || undefined,
      page,
      pageSize: 50,
    },
  );

  const totalPages = data ? Math.ceil(data.total / 50) : 1;

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={() =>
            void navigate({
              to: "/companies/$companyId/contabilidad/mayor",
              params: { companyId },
            })
          }
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-xl font-semibold">
            {t("journal.movements.title", { code: accountCode })}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t("journal.movements.subtitle")}
          </p>
        </div>
      </div>

      <div className="flex gap-3">
        <Input
          type="date"
          className="h-9 w-36"
          value={periodFrom}
          onChange={(e) => { setPeriodFrom(e.target.value); setPage(1); }}
        />
        <Input
          type="date"
          className="h-9 w-36"
          value={periodTo}
          onChange={(e) => { setPeriodTo(e.target.value); setPage(1); }}
        />
      </div>

      {isError ? (
        <ErrorState title={t("journal.error.loadMovements")} />
      ) : isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </div>
      ) : (
        <AccountMovementsTable
          movements={data?.movements ?? []}
          companyId={companyId}
        />
      )}

      {(data?.total ?? 0) > 50 ? (
        <div className="flex items-center justify-between">
          <p className="text-xs text-muted-foreground">
            {t("common.pagination.showing", {
              from: Math.min((page - 1) * 50 + 1, data?.total ?? 0),
              to: Math.min(page * 50, data?.total ?? 0),
              total: data?.total ?? 0,
            })}
          </p>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
              {t("common.pagination.prev")}
            </Button>
            <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
              {t("common.pagination.next")}
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
